import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { FormDef, FormFieldDef, FormFieldType, FormFieldWidth, FormSubmissionSummary, FormSubmissionDetail, FormAllowedDomain } from '@evo-hub/shared';

export interface FormListItem extends FormDef {
  fieldCount: number;
  submissionCount: number;
  pendingCount: number;
}

export interface FieldInput {
  label: string;
  fieldType: FormFieldType;
  required?: boolean;
  options?: string[];
  placeholder?: string | null;
  width?: FormFieldWidth;
}

// ── Forms ─────────────────────────────────────────────────────────────────────

export function useForms() {
  return useQuery<{ forms: FormListItem[] }>({
    queryKey: ['forms'],
    queryFn: () => api.get<{ forms: FormListItem[] }>('/api/forms'),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useForm(id: string | undefined) {
  return useQuery<{ form: FormDef; fields: FormFieldDef[] }>({
    queryKey: ['forms', id],
    queryFn: () => api.get<{ form: FormDef; fields: FormFieldDef[] }>(`/api/forms/${id}`),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useCreateForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string; contactId?: number }) => api.post<FormDef>('/api/forms', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }),
  });
}

export function useUpdateForm() {
  const qc = useQueryClient();
  return useMutation({
    // themePrimary/themeText: hex o null (null = volver al default). logoPath
    // sólo acepta null — el path real lo escribe la subida (useUploadFormLogo).
    mutationFn: ({ id, ...input }: {
      id: string; name?: string; description?: string | null; enabled?: boolean;
      submitLabel?: string | null; thankYouMessage?: string | null;
      /** Embudo al que caen las respuestas. null = ninguno. */
      funnelId?: number | null;
      themePrimary?: string | null; themeText?: string | null; themeBg?: string | null; themePageBg?: string | null; logoPath?: null;
    }) => api.patch<FormDef>(`/api/forms/${id}`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['forms'] });
      void qc.invalidateQueries({ queryKey: ['forms', vars.id] });
    },
  });
}

/** `withSubmissions`: borra también las respuestas (el servidor guarda una
 *  copia antes). Sin eso, un formulario con respuestas devuelve 409. */
export function useDeleteForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, withSubmissions = false }: { id: string; withSubmissions?: boolean }) =>
      api.delete<{ ok: boolean; submissionCount: number; backupPath: string | null }>(
        `/api/forms/${encodeURIComponent(id)}${withSubmissions ? '?withSubmissions=1' : ''}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }),
  });
}

export function useSaveFormFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: FieldInput[] }) =>
      api.put<{ fields: FormFieldDef[] }>(`/api/forms/${id}/fields`, { fields }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['forms', vars.id] });
      void qc.invalidateQueries({ queryKey: ['forms'] });
    },
  });
}

export interface ImportFormParseResult {
  name: string | null;
  description: string | null;
  fields: FieldInput[];
  /** Campos de lista importados sin opciones porque no estaban en el original.
   *  Se avisan en pantalla; nunca se completan por inferencia. */
  fieldsMissingOptions: string[];
  /** URL final descargada (tras redirects) cuando se importó por link. */
  sourceUrl: string | null;
}

/** Texto/HTML pegado, o link público del formulario. */
export type ImportFormInput = { content: string } | { url: string };

export function useImportFormParse() {
  return useMutation({
    mutationFn: (input: ImportFormInput) => api.post<ImportFormParseResult>('/api/forms/import/parse', input),
  });
}

export function usePendingFormsCount() {
  return useQuery<{ count: number }>({
    queryKey: ['forms', 'pending-count'],
    queryFn: () => api.get<{ count: number }>('/api/forms/pending-count'),
    // Contador de badge: entra un formulario cuando alguien lo completa desde
    // afuera, así que sí necesita intervalo (no hay acción local que lo
    // dispare). 60s es razonable para un badge.
    refetchInterval: 60_000,
  });
}

// ── Dominios autorizados (Etapa 2) ──────────────────────────────────────────────

export function useFormDomains(formId: string | undefined) {
  return useQuery<{ domains: FormAllowedDomain[] }>({
    queryKey: ['forms', formId, 'domains'],
    queryFn: () => api.get<{ domains: FormAllowedDomain[] }>(`/api/forms/${formId}/domains`),
    enabled: !!formId,
  });
}

export function useAddFormDomain(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domain: string) => api.post<FormAllowedDomain>(`/api/forms/${formId}/domains`, { domain }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms', formId, 'domains'] }),
  });
}

export function useRemoveFormDomain(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domainId: number) => api.delete<{ ok: boolean }>(`/api/forms/${formId}/domains/${domainId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms', formId, 'domains'] }),
  });
}

// ── Submissions ───────────────────────────────────────────────────────────────

export function useFormSubmissions(formId: string | undefined) {
  return useQuery<{ submissions: FormSubmissionSummary[] }>({
    queryKey: ['forms', formId, 'submissions'],
    queryFn: () => api.get<{ submissions: FormSubmissionSummary[] }>(`/api/forms/${formId}/submissions`),
    enabled: !!formId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useFormSubmission(id: number | undefined) {
  return useQuery<FormSubmissionDetail>({
    queryKey: ['forms', 'submission', id],
    queryFn: () => api.get<FormSubmissionDetail>(`/api/forms/submissions/${id}`),
    enabled: id != null,
  });
}

function invalidateSubmissions(qc: ReturnType<typeof useQueryClient>, formId?: string) {
  void qc.invalidateQueries({ queryKey: ['forms'] });
  if (formId) void qc.invalidateQueries({ queryKey: ['forms', formId, 'submissions'] });
}

/**
 * Leído / no leído en lote, sobre respuestas.
 *
 * Marcar como NO leída es lo que permite dejar algo pendiente después de
 * haberlo abierto de casualidad — sin eso, abrir una respuesta la daba por
 * atendida para siempre.
 */
export function useBulkSetSubmissionsRead(formId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ submissionIds, read }: { submissionIds: number[]; read: boolean }) =>
      api.post<{ updated: number; read: boolean }>('/api/forms/submissions/bulk/read', { submissionIds, read }),
    onSuccess: () => {
      invalidateSubmissions(qc, formId);
      // El contador del menú cuenta respuestas sin leer.
      void qc.invalidateQueries({ queryKey: ['nav-badges'] });
    },
  });
}

/** Lo mismo desde la lista de formularios: alcanza a todas las respuestas. */
export function useBulkSetFormsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ formIds, read }: { formIds: string[]; read: boolean }) =>
      api.post<{ updated: number; read: boolean }>('/api/forms/bulk/read', { formIds, read }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['forms'] });
      void qc.invalidateQueries({ queryKey: ['nav-badges'] });
    },
  });
}

export function useMarkSubmissionReviewed(formId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<FormSubmissionSummary>(`/api/forms/submissions/${id}/review`),
    onSuccess: () => invalidateSubmissions(qc, formId),
  });
}

export function useDismissSubmissions(formId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (submissionIds: number[]) => api.post<{ dismissed: number }>('/api/forms/submissions/bulk/dismiss', { submissionIds }),
    onSuccess: () => invalidateSubmissions(qc, formId),
  });
}

export function useUndismissSubmissions(formId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (submissionIds: number[]) => api.post<{ restored: number }>('/api/forms/submissions/bulk/undismiss', { submissionIds }),
    onSuccess: () => invalidateSubmissions(qc, formId),
  });
}

export function useLinkSubmissionsToContact(formId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ submissionIds, contactId }: { submissionIds: number[]; contactId: number }) =>
      api.post<{ linked: number }>('/api/forms/submissions/bulk/link', { submissionIds, contactId }),
    onSuccess: () => invalidateSubmissions(qc, formId),
  });
}

export function useCreateContactsFromSubmissions(formId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (submissionIds: number[]) =>
      api.post<{ created: Array<{ channelId: number; contactId: number }> }>('/api/forms/submissions/bulk/create-contact', { submissionIds }),
    onSuccess: () => invalidateSubmissions(qc, formId),
  });
}

export function useCreateLeadsFromSubmissions(formId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (submissionIds: number[]) =>
      api.post<{ created: Array<{ channelId: number; contactId: number; leadId: string }> }>('/api/forms/submissions/bulk/create-lead', { submissionIds }),
    onSuccess: () => invalidateSubmissions(qc, formId),
  });
}

export function useUnlinkSubmissions(formId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (submissionIds: number[]) => api.post<{ unlinked: number }>('/api/forms/submissions/bulk/unlink', { submissionIds }),
    onSuccess: () => invalidateSubmissions(qc, formId),
  });
}

// ── Indicadores del menú ─────────────────────────────────────────────────────
// Un solo endpoint para todos los badges del sidebar: cinco consultas por
// minuto para un puñado de números no valía la pena.
// El tipo y la lista vienen de @evo-hub/shared. Estaban escritos a mano acá y
// en el servidor: dos listas paralelas que nada comparaba, así que agregar un
// módulo en una sola compilaba igual y el indicador no aparecía nunca.
export type { NavBadgeCounts, NavModule } from '@evo-hub/shared';
import type { NavBadgeCounts, NavModule } from '@evo-hub/shared';

export function useNavBadges() {
  return useQuery<NavBadgeCounts>({
    queryKey: ['nav-badges'],
    queryFn: () => api.get<NavBadgeCounts>('/api/nav-badges'),
    // Lo que cambia acá lo dispara el worker o alguien de afuera (entra un
    // correo, se completa un formulario), no una acción local: hace falta
    // intervalo. 60s, como los badges que ya existían.
    refetchInterval: 60_000,
  });
}

/**
 * Entrar a un módulo lo marca visto: el badge baja a cero.
 *
 * Devuelve la marca ANTERIOR — la lista la necesita para resaltar las filas que
 * eran nuevas en esta visita. Si solo apagáramos el contador, en el mismo
 * instante se perdería cuáles eran.
 */
export function useMarkNavSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (module: NavModule) =>
      api.post<{ previo: string; vistos: string[]; noLeidos?: string[] }>(`/api/nav-badges/seen/${module}`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['nav-badges'] }); },
  });
}

/**
 * Abrir un ítem apaga SU marca de fila.
 *
 * No invalida los badges: el contador ya se apagó al entrar al módulo, y una
 * consulta por cada fila que se abre es ruido.
 */
export function useMarkNavItemSeen() {
  return useMutation({
    mutationFn: ({ module, itemId }: { module: NavModule; itemId: string }) =>
      api.post(`/api/nav-badges/seen/${module}/item/${encodeURIComponent(itemId)}`, {}),
  });
}

/**
 * Leído / no leído para varios ítems — la barra de acciones de una lista.
 *
 * Este SÍ invalida los badges: un "no leído" a mano cuenta en el menú aunque
 * ya se haya entrado al módulo, y marcarlo leído lo descuenta.
 */
export function useMarkNavItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ module, itemIds, leido }: { module: NavModule; itemIds: string[]; leido: boolean }) =>
      api.post<{ ok: boolean; count: number }>(`/api/nav-badges/seen/${module}/items`, { itemIds, leido }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['nav-badges'] }); },
  });
}
